// src/pages/paidProject/Annexure 1/FinancialIndicators.jsx

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from "@mui/material";
import API from "../../../api";

// ─── Depreciation helper ───────────────────────────────────
function calcFullYear(cost, rate, yearIndex) {
  if (yearIndex === 0) return 0;
  let remaining = cost,
    dep = 0;
  for (let i = 1; i <= yearIndex; i++) {
    dep = (remaining * rate) / 100;
    remaining -= dep;
  }
  return dep;
}

// ─── Month‐label generator ──────────────────────────────────
function generateMonthLabels(startStr, length) {
  if (!startStr || !startStr.includes("-")) return [];
  const [startM, startY] = startStr.split("-").map(Number);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  let m = startM - 1,
    y = startY;
  return Array.from({ length }, () => {
    const label = `${months[m]}-${y}`;
    m = (m + 1) % 12;
    if (m === 0) y++;
    return label;
  });
}

export default function FinancialIndicators() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);

  // ─── 2) Fetch API inputs ─────────────────────────────────
  useEffect(() => {
    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => setData(data))
      .catch((err) => console.error(err));
  }, [projectId]);

  // ─── 3) Destructure API data ─────────────────────────────
  const {
    projectYears = [],
    depreciationData = [],
    projectedProfitabilityInputs = {},
    workingCapitalInputs = {},
    costProjectInputs = {},
    plantMachineryData = [],
    plantContingencies = 0,
    plantGstPercent = 0,
    furnitureAndFixtures = [],
    otherFixedAssets = [],
    computers = [],
    preliminaryExpenses = [],
    netProfitAfterTax: netProfitAfterTaxDB = [],
    totalAnnualSales: totalAnnualSalesLS = [],
    grandTotalCostOfProduction: costProdLS = [],
    averageCostOfProdPerMT: avgCostPerMTLS = [],
    workingCapitalBank: workingCapitalBankLS = [],
    totalClosingStock: closingPerYearLS = [],
  } = data || {};

  // ─── 4) Adjustment percentages ───────────────────────────
  const leaseRent = +projectedProfitabilityInputs.leaseRent || 0;
  const sellingPct =
    +projectedProfitabilityInputs.sellingAndDistributionExpense || 0;
  const prelimWrittenOff =
    +projectedProfitabilityInputs.preliminaryExpensesWrittenOff || 0;
  const provisionForTaxPct = +projectedProfitabilityInputs.provisionForTax || 0;
  const ccPct = +workingCapitalInputs.interestOnCC || 0;

  // ─── 5) Depreciation-cost lookup ──────────────────────────
  const getCostByParticular = useCallback(
    (p) => {
      const keyMap = {
        "Land & Site Development (On Lease)": "landDevelopment",
        "Civil Construction (On Lease)": "civilConstruction",
      };
      if (keyMap[p] && costProjectInputs[keyMap[p]] != null) {
        return +costProjectInputs[keyMap[p]] || 0;
      }
      switch (p) {
        case "Plant & Machinery": {
          const sub = plantMachineryData.reduce(
            (s, r) => s + (+r.amount || 0),
            0
          );
          const cont = (sub * (+plantContingencies || 0)) / 100;
          const tax = ((sub + cont) * (+plantGstPercent || 0)) / 100;
          return sub + cont + tax;
        }
        case "Furniture and Fixture":
          return furnitureAndFixtures.reduce((s, r) => s + (+r.amount || 0), 0);
        case "Miscellaneous Fixed Assets":
          return otherFixedAssets.reduce((s, r) => s + (+r.amount || 0), 0);
        case "Computer and Software":
          return computers.reduce((s, r) => s + (+r.amount || 0), 0);
        case "Preliminary Expenses":
          return preliminaryExpenses.reduce((s, r) => s + (+r.amount || 0), 0);
        default:
          return 0;
      }
    },
    [
      costProjectInputs,
      plantMachineryData,
      plantContingencies,
      plantGstPercent,
      furnitureAndFixtures,
      otherFixedAssets,
      computers,
      preliminaryExpenses,
    ]
  );

  // ─── 6) Full-year depreciation per FY ────────────────────
  const depreciationTotals = useMemo(
    () =>
      projectYears.map((_, yi) =>
        depreciationData.reduce(
          (sum, row) =>
            sum +
            calcFullYear(
              getCostByParticular(row.particular),
              row.taxPercent,
              yi
            ),
          0
        )
      ),
    [projectYears, depreciationData, getCostByParticular]
  );

  // ─── 7) FG stock, COGS & gross profit ────────────────────
  const closingStockFG = useMemo(
    () =>
      projectYears.map(
        (_, i) =>
          +((closingPerYearLS[i] * avgCostPerMTLS[i]) / 100000).toFixed(2)
      ),
    [projectYears, closingPerYearLS, avgCostPerMTLS]
  );
  const openingStockFG = useMemo(
    () => closingStockFG.map((v, i) => (i > 0 ? closingStockFG[i - 1] : 0)),
    [closingStockFG]
  );
  const netCOGS = useMemo(
    () =>
      projectYears.map(
        (_, i) =>
          +(costProdLS[i] + openingStockFG[i] - closingStockFG[i]).toFixed(2)
      ),
    [projectYears, costProdLS, openingStockFG, closingStockFG]
  );
  const grossProfit = useMemo(
    () =>
      projectYears.map(
        (_, i) => +(totalAnnualSalesLS[i] - netCOGS[i]).toFixed(2)
      ),
    [projectYears, totalAnnualSalesLS, netCOGS]
  );

  // ─── 8) SG&A & CC charges ────────────────────────────────
  const sgaExpenses = useMemo(
    () =>
      projectYears.map(
        (_, i) => +((totalAnnualSalesLS[i] * sellingPct) / 100).toFixed(2)
      ),
    [projectYears, totalAnnualSalesLS, sellingPct]
  );
  const ccCharges = useMemo(
    () => workingCapitalBankLS.map((b) => +((b * ccPct) / 100).toFixed(2)),
    [workingCapitalBankLS, ccPct]
  );

  // ─── 9) Finance charges summary ──────────────────────────
  const financeSummary = useMemo(() => {
    const term = data?.termLoanInput || {};
    let emi = Array.isArray(term.emiSchedule) ? term.emiSchedule : [];
    /* if (!emi.length) {
      const local = JSON.parse(localStorage.getItem('emiSchedule') || '[]');
      if (Array.isArray(local) && local.length) emi = local;
    } */
    const rawStart = term.pStartMonth?.includes("-")
      ? term.pStartMonth
      : term.startMonth || "";
    if (!emi.length || !rawStart) return [];
    const labels = generateMonthLabels(rawStart, emi.length);
    const monthsMap = {
      Jan: 1,
      Feb: 2,
      Mar: 3,
      Apr: 4,
      May: 5,
      Jun: 6,
      Jul: 7,
      Aug: 8,
      Sep: 9,
      Oct: 10,
      Nov: 11,
      Dec: 12,
    };
    const bucket = {};
    emi.forEach((row, idx) => {
      const [monStr, yrStr] = (labels[idx] || "").split("-");
      const mon = monthsMap[monStr] || 0;
      const yr = parseInt(yrStr, 10) || 0;
      const fy0 = mon >= 4 ? yr : yr - 1;
      const fyLabel = `${fy0}-${String(fy0 + 1).slice(-2)}`;
      if (!bucket[fyLabel]) {
        bucket[fyLabel] = {
          totalPrincipal: 0,
          totalInterest: 0,
          closingBalance: 0,
        };
      }
      bucket[fyLabel].totalPrincipal += row.principal || 0;
      bucket[fyLabel].totalInterest += row.interest || 0;
      bucket[fyLabel].closingBalance = row.closingBalance || 0;
    });
    return Object.values(bucket);
  }, [data]);

  const termLoanCharges = useMemo(
    () => financeSummary.map((fy) => fy.totalInterest || 0),
    [financeSummary]
  );
  const termLoanClosingBalance = useMemo(
    () => financeSummary.map((fy) => fy.closingBalance || 0),
    [financeSummary]
  );

  // ─── 10) PBT → PAT ───────────────────────────────────────
  const totalAdjustments = useMemo(
    () =>
      projectYears.map((_, i) => {
        const lease = i > 0 ? leaseRent : 0;
        const sga = sgaExpenses[i] || 0;
        const dep = depreciationTotals[i] || 0;
        const prelim = i > 0 ? prelimWrittenOff : 0;
        const cc = ccCharges[i] || 0;
        const termI = termLoanCharges[i] || 0;
        return +(lease + sga + dep + prelim + cc + termI).toFixed(2);
      }),
    [
      projectYears,
      leaseRent,
      sgaExpenses,
      depreciationTotals,
      prelimWrittenOff,
      ccCharges,
      termLoanCharges,
    ]
  );

  const netProfitBeforeTax = useMemo(
    () =>
      projectYears.map(
        (_, i) => +(grossProfit[i] - totalAdjustments[i]).toFixed(2)
      ),
    [projectYears, grossProfit, totalAdjustments]
  );

  const provisionForTax = useMemo(
    () =>
      netProfitBeforeTax.map(
        (npt) => +((npt * provisionForTaxPct) / 100).toFixed(2)
      ),
    [netProfitBeforeTax, provisionForTaxPct]
  );

  // ─── 11) Pull PAT from DB ────────────────────────────────
  const netProfitAfterTax = useMemo(
    () => netProfitAfterTaxDB.map((v) => +v || 0),
    [netProfitAfterTaxDB]
  );

  // ─── 12) Dummy proprietor’s capital ──────────────────────
  const dummyCapital = useMemo(
    () => projectYears.map(() => 76.64),
    [projectYears]
  );

  // ─── 13) Indicators ──────────────────────────────────────
  const netProfitRatio = projectYears.map((_, i) =>
    totalAnnualSalesLS[i] ? netProfitAfterTax[i] / totalAnnualSalesLS[i] : 0
  );
  const disposalOfSurplus = projectYears.map(
    (_, i) => netProfitAfterTax[i] + termLoanCharges[i] + depreciationTotals[i]
  );
  const principalComponent = financeSummary.map((fy) => fy.totalPrincipal || 0);
  const loanTotals = principalComponent.map(
    (p, i) => p + (termLoanCharges[i] || 0)
  );
  const DSCR = projectYears.map((_, i) =>
    loanTotals[i] ? disposalOfSurplus[i] / loanTotals[i] : 0
  );
  const totalDebtArr = termLoanClosingBalance;
  const DEratio = projectYears.map((_, i) =>
    dummyCapital[i] ? totalDebtArr[i] / dummyCapital[i] : 0
  );
  const avgDERatio = DEratio.reduce((s, v) => s + v, 0) / DEratio.length || 0;
  const TOL_TNW_Ratio = projectYears.map((_, i) =>
    dummyCapital[i] ? totalDebtArr[i] / dummyCapital[i] : 0
  );

  if (!data) return <Typography>Loading...</Typography>;

  const fmt = (x) =>
    isNaN(x) ? "-" : x.toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <Box p={2}>
      {/* Net Profit Ratio */}
      <Typography variant="h6" gutterBottom>
        CALCULATION OF NET PROFIT RATIO
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Particulars</TableCell>
              {projectYears.map((yr) => (
                <TableCell key={yr}>{yr}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Net Profit after Tax</TableCell>
              {netProfitAfterTax.map((v, i) => (
                <TableCell key={i}>{fmt(v)}</TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>Sales</TableCell>
              {totalAnnualSalesLS.map((v, i) => (
                <TableCell key={i}>{fmt(v)}</TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>Net Profit Ratio</TableCell>
              {netProfitRatio.map((v, i) => (
                <TableCell key={i}>{(v * 100).toFixed(2)}%</TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* DSCR */}
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          CALCULATION OF DEBT SERVICE COVERAGE RATIO
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Particulars</TableCell>
                {projectYears.map((yr) => (
                  <TableCell key={yr}>{yr}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={projectYears.length + 1}
                  sx={{ fontWeight: "bold" }}
                >
                  A. Disposal of Surplus
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Net Profit after Tax</TableCell>
                {netProfitAfterTax.map((v, i) => (
                  <TableCell key={i}>{fmt(v)}</TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>Total Interest (Rs)</TableCell>
                {termLoanCharges.map((v, i) => (
                  <TableCell key={i}>{fmt(v)}</TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>Depreciation</TableCell>
                {depreciationTotals.map((v, i) => (
                  <TableCell key={i}>{fmt(v)}</TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>Total</TableCell>
                {disposalOfSurplus.map((v, i) => (
                  <TableCell key={i}>{fmt(v)}</TableCell>
                ))}
              </TableRow>

              <TableRow>
                <TableCell
                  colSpan={projectYears.length + 1}
                  sx={{ fontWeight: "bold" }}
                >
                  B. Loan Component
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Principal Component payable per year</TableCell>
                {principalComponent.map((v, i) => (
                  <TableCell key={i}>{fmt(v)}</TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>Interest component payable</TableCell>
                {termLoanCharges.map((v, i) => (
                  <TableCell key={i}>{fmt(v)}</TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>Total</TableCell>
                {loanTotals.map((v, i) => (
                  <TableCell key={i}>{fmt(v)}</TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>DSCR (A/B)</TableCell>
                {DSCR.map((v, i) => (
                  <TableCell key={i}>{v.toFixed(4)}</TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Debt to Equity Ratio */}
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          CALCULATION OF DEBT TO EQUITY RATIO
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Particulars</TableCell>
                {projectYears.map((yr) => (
                  <TableCell key={yr}>{yr}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={projectYears.length + 1}
                  sx={{ fontWeight: "bold" }}
                >
                  A. Total Debt
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Term Loan</TableCell>
                {termLoanClosingBalance.map((v, i) => (
                  <TableCell key={i}>{fmt(v)}</TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>Total</TableCell>
                {termLoanClosingBalance.map((v, i) => (
                  <TableCell key={i}>{fmt(v)}</TableCell>
                ))}
              </TableRow>

              <TableRow>
                <TableCell
                  colSpan={projectYears.length + 1}
                  sx={{ fontWeight: "bold" }}
                >
                  B. Equity
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Capital &amp; Reserves (Proprietor’s Capital)
                </TableCell>
                {dummyCapital.map((v, i) => (
                  <TableCell key={i}>{fmt(v)}</TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>Total</TableCell>
                {dummyCapital.map((v, i) => (
                  <TableCell key={i}>{fmt(v)}</TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>DE Ratio (A/B)</TableCell>
                {DEratio.map((v, i) => (
                  <TableCell key={i}>{v.toFixed(4)}</TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>Average DE Ratio</TableCell>
                <TableCell colSpan={projectYears.length}>
                  {avgDERatio.toFixed(4)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* TOL / TNW Ratio */}
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          CALCULATION OF TOL / TNW RATIO
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Particulars</TableCell>
                {projectYears.map((yr) => (
                  <TableCell key={yr}>{yr}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={projectYears.length + 1}
                  sx={{ fontWeight: "bold" }}
                >
                  A. Total Outside Liability
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Term Loan</TableCell>
                {termLoanClosingBalance.map((v, i) => (
                  <TableCell key={i}>{fmt(v)}</TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>Total</TableCell>
                {termLoanClosingBalance.map((v, i) => (
                  <TableCell key={i}>{fmt(v)}</TableCell>
                ))}
              </TableRow>

              <TableRow>
                <TableCell
                  colSpan={projectYears.length + 1}
                  sx={{ fontWeight: "bold" }}
                >
                  B. Total Net Worth
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Proprietor’s Capital</TableCell>
                {dummyCapital.map((v, i) => (
                  <TableCell key={i}>{fmt(v)}</TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>Total</TableCell>
                {dummyCapital.map((v, i) => (
                  <TableCell key={i}>{fmt(v)}</TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>TOL / TNW Ratio (A/B)</TableCell>
                {TOL_TNW_Ratio.map((v, i) => (
                  <TableCell key={i}>{v.toFixed(4)}</TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
